import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { BusinessCard } from '../types';

export interface Subscription {
  id: string;
  plan: 'free' | 'pro' | 'teams' | 'enterprise';
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
  currentPeriodEnd: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  startDate: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  isVerified: boolean;
  isPremium: boolean;
  isSuperAdmin: boolean;
  token?: string;
  subscription?: Subscription;
  businessCard: any | null;
  cards: BusinessCard[];
  contacts: any[];
  lastLoginAt: string | null;
  loginCount: number;
  createdAt: string;
  updatedAt: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

type AuthAction = 
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'AUTH_UNAVAILABLE' }
  | { type: 'UPDATE_SUBSCRIPTION'; payload: Subscription };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('auth_token'),
  loading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null };
    case 'AUTH_SUCCESS':
      localStorage.setItem('auth_token', action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      localStorage.removeItem('auth_token');
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      localStorage.removeItem('auth_token');
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'UPDATE_USER':
      return { ...state, user: action.payload, loading: false };
    case 'AUTH_UNAVAILABLE':
      // Server temporarily unreachable: stop loading but keep the stored session.
      return { ...state, loading: false };
    case 'UPDATE_SUBSCRIPTION':
      if (state.user) {
        return { ...state, user: { ...state.user, subscription: action.payload } };
      }
      return state;
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (credentialToken: string) => Promise<void>;
  register: (name: string, email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  updateSubscription: (subscription: Subscription) => void;
  getAuthToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Use same origin for Vercel deployment, fallback to localhost for dev
const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' ? '' : 'http://localhost:3001');

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = async (email: string, password: string) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: data.user,
          token: data.token,
        },
      });
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Login failed',
      });
      throw error;
    }
  };

  const loginWithGoogle = async (credentialToken: string) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credentialToken }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Google login failed');
      }

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: data.user,
          token: data.token,
        },
      });
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Google login failed',
      });
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string, confirmPassword: string) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password, confirmPassword }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      // Registration successful but user needs to verify email
      dispatch({ type: 'AUTH_LOGOUT' });
      return data;
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Registration failed',
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (state.token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${state.token}`,
          },
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const refreshUser = async () => {
    if (!state.token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${state.token}`,
        },
        credentials: 'include',
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        dispatch({ type: 'UPDATE_USER', payload: data.user });
      } else if (response.status === 401) {
        dispatch({ type: 'AUTH_LOGOUT' });
      } else {
        // 5xx / maintenance: don't throw away a valid session.
        dispatch({ type: 'AUTH_UNAVAILABLE' });
      }
    } catch (error) {
      console.warn('Could not reach the server to restore your session.');
      dispatch({ type: 'AUTH_UNAVAILABLE' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const updateSubscription = (subscription: Subscription) => {
    dispatch({ type: 'UPDATE_SUBSCRIPTION', payload: subscription });
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (state.token) {
        await refreshUser();
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    checkAuth();
  }, []);

  const getAuthToken = () => state.token;

  const value = {
    ...state,
    login,
    loginWithGoogle,
    register,
    logout,
    clearError,
    refreshUser,
    updateSubscription,
    getAuthToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};